import React, { useState } from 'react';

const sections = [
  {
    title: 'Getting Started',
    icon: '🚀',
    content: [
      {
        heading: 'Creating Your First Show',
        text: 'Start on the Saved Shows page. Click the "New Show" button, give your show a name, and it will appear in your show list. From there, navigate to Job Info to begin filling out the details of your event. All changes save automatically as you work.'
      },
      {
        heading: 'Navigating Between Pages',
        text: 'Use the tab bar across the top to move between pages. Each tab corresponds to a different part of your show. Work from left to right — start with Job Info, then build your show flow, then check the Faces page before your event.'
      }
    ]
  },
  {
    title: 'Job Info',
    icon: '📋',
    content: [
      {
        heading: 'Event Details',
        text: 'Fill in all the top-level details about your event: job title, setup dates, show dates, doors open time, show start time, and crew call time. These fields feed into the Show Flow header automatically.'
      },
      {
        heading: 'Zoom & Streaming Info',
        text: 'Enter your Zoom Webinar ID and Passcode, Zoom Presenter ID and Passcode, and any stream keys for your webcast. This information will appear in the Show Flow header rows for quick reference during the event.'
      },
      {
        heading: 'Presenter List',
        text: 'Add presenters in the order they appear during the event. Select them from the Presenter Database. This list feeds both the Faces page (their photos and titles) and the presenter dropdown in the Show Flow grid.'
      }
    ]
  },
  {
    title: 'Show Flow',
    icon: '🎬',
    content: [
      {
        heading: 'Adding Rows',
        text: 'Click the "Add Row" button to add a new line item to your show flow. Each row represents one segment of your event. Rows are automatically numbered starting from 0.'
      },
      {
        heading: 'Automatic Time Calculation',
        text: 'Enter a start time in the first row (e.g., 8:00 AM) and a duration in minutes. The next row will automatically calculate its start time based on the previous row\'s start time plus duration. Changing any time or duration cascades through all rows below it automatically.'
      },
      {
        heading: 'Row Color Coding',
        text: 'Rows automatically change color based on their content. If the description contains the word "video" or "playback", the row turns yellow. If it contains "break" or "lunch", the row turns blue. All other rows alternate between white and light gray for easy reading.'
      },
      {
        heading: 'Dropdown Columns',
        text: 'Columns like Main Screens, DSM 1, DSM 2, Audio Cues, Lighting Cues, and Stage Logistics have dropdown menus with common options. You can also type in any value manually. Manage the dropdown options on the Lists page.'
      },
      {
        heading: 'Exporting',
        text: 'Use the Export to PDF button to generate a print-ready PDF of your show flow. Use Export to Excel to generate an .xlsx spreadsheet. Both exports include all rows and formatting.'
      }
    ]
  },
  {
    title: 'Faces Page',
    icon: '👤',
    content: [
      {
        heading: 'What It Shows',
        text: 'The Faces page automatically displays all presenters from your Job Info presenter list. Each presenter shows their photo, name, and job title. 12 presenters fit on each printed page in a 3-column by 4-row grid.'
      },
      {
        heading: 'Printing',
        text: 'Click the Print button to open the browser print dialog. The page is formatted specifically for 8.5 x 11 inch paper. All navigation and UI elements are hidden automatically when printing — only the presenter grid appears.'
      },
      {
        heading: 'Missing Photos',
        text: 'If a presenter does not have a photo in the database, a placeholder silhouette will appear in their slot. Add their photo through the Database page and it will update automatically.'
      }
    ]
  },
  {
    title: 'Lists',
    icon: '📝',
    content: [
      {
        heading: 'Managing Dropdown Options',
        text: 'The Lists page controls what appears in the dropdown menus throughout the Show Flow grid. Each column that has a dropdown is represented here as its own section.'
      },
      {
        heading: 'Adding Items',
        text: 'Type a new value into the input field next to any list and press Enter or click Add. The new item will immediately appear in that column\'s dropdown across all shows.'
      },
      {
        heading: 'Removing Items',
        text: 'Click the X button next to any item to remove it from the list. Note: removing an item does not delete it from existing show flow rows that already use it — it only removes it from the dropdown going forward.'
      },
      {
        heading: 'Reordering Items',
        text: 'Drag and drop any item to change its position in the dropdown list. The order here determines the order items appear in the dropdown menus.'
      }
    ]
  },
  {
    title: 'Database',
    icon: '🗄️',
    content: [
      {
        heading: 'Adding Presenters',
        text: 'Click "Add Presenter" to open the form. Fill in their name, job title, company, and department, then upload a photo. Supported photo formats are JPG and PNG. Photos are automatically cropped to a square.'
      },
      {
        heading: 'Filtering',
        text: 'Use the Company and Department dropdown filters at the top of the page to narrow down the list. This is especially useful when managing a large database of presenters across multiple companies and departments.'
      },
      {
        heading: 'Editing Presenters',
        text: 'Click on any presenter card to open their edit form. You can update any field or replace their photo. Changes take effect immediately across all shows that use this presenter.'
      },
      {
        heading: 'Deleting Presenters',
        text: 'Click the delete button on any presenter card. You will be asked to confirm before deletion. If the presenter is currently used in an active show, you will see a warning.'
      }
    ]
  },
  {
    title: 'Tips & Shortcuts',
    icon: '💡',
    content: [
      {
        heading: 'Auto-Save',
        text: 'Everything saves automatically. There is no Save button. As soon as you click away from a field or make a change, it is saved to your show project.'
      },
      {
        heading: 'Show Flow Version Numbers',
        text: 'Get in the habit of incrementing the version number in the Show Flow header every time you distribute an updated flow to your team. This prevents confusion about which version is current on show day.'
      },
      {
        heading: 'Building Your Lists Before the Show',
        text: 'Before your first event, spend 10 minutes on the Lists page setting up your standard dropdown options for Main Screens, Audio Cues, Lighting Cues, and Stage Logistics. This makes building show flows much faster.'
      },
      {
        heading: 'Presenter Database is Global',
        text: 'The presenter database is shared across all shows. Add a presenter once and they are available for every future show. Build it up over time and it becomes a valuable asset.'
      }
    ]
  }
];

function Directions() {
  const [openSection, setOpenSection] = useState(null);

  const toggle = (index) => {
    setOpenSection(openSection === index ? null : index);
  };

  return (
    <div>
      <div className="page-header">
        <h1>Directions</h1>
        <p>How to use every part of the Show Flow Creator.</p>
      </div>

      <div className="card" style={{ marginBottom: '16px', backgroundColor: '#ebf8ff', border: '1px solid #bee3f8' }}>
        <p style={{ color: '#2b6cb0', fontSize: '15px' }}>
          💡 <strong>Tip:</strong> Click any section below to expand it and read the directions for that page.
        </p>
      </div>

      {sections.map((section, index) => (
        <div key={index} className="card" style={{ marginBottom: '12px', padding: '0' }}>
          <button
            onClick={() => toggle(index)}
            style={{
              width: '100%',
              background: 'none',
              border: 'none',
              padding: '20px 24px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              cursor: 'pointer',
              fontSize: '16px',
              fontWeight: '600',
              color: '#1a1a2e',
              textAlign: 'left'
            }}
          >
            <span>{section.icon} &nbsp; {section.title}</span>
            <span style={{ fontSize: '20px', color: '#4299e1' }}>
              {openSection === index ? '▲' : '▼'}
            </span>
          </button>

          {openSection === index && (
            <div style={{ padding: '0 24px 24px 24px', borderTop: '1px solid #e2e8f0' }}>
              {section.content.map((item, i) => (
                <div key={i} style={{ marginTop: '20px' }}>
                  <h3 style={{ fontSize: '15px', fontWeight: '600', color: '#2d3748', marginBottom: '6px' }}>
                    {item.heading}
                  </h3>
                  <p style={{ fontSize: '14px', color: '#718096', lineHeight: '1.7' }}>
                    {item.text}
                  </p>
                </div>
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

export default Directions;